import { useEffect, useRef } from 'react';

const STORAGE_KEY = 'posLastActivityAt';
const TICK_MS = 5_000;

const readLastActivityAt = (): number => {
  try {
    const raw = Number(localStorage.getItem(STORAGE_KEY));
    return Number.isFinite(raw) && raw > 0 ? raw : Date.now();
  } catch {
    return Date.now();
  }
};

const writeLastActivityAt = (value: number): void => {
  try {
    localStorage.setItem(STORAGE_KEY, String(value));
  } catch {
    // Private mode: the in-memory ref still drives the timer for this tab.
  }
};

// Summary: Returns the device to the employee picker after `minutes` with no screen interaction.
// Only the device can observe "nobody touched it" — request traffic keeps flowing without a human
// (SSE reconnects, refreshes, other devices' events), so the timer lives here, not on the server.
// `minutes = 0` disables the feature entirely.
export function useEmployeeIdleLogout(
  minutes: number,
  isActive: boolean,
  isSafeToLogout: boolean,
  onIdleLogout: () => void,
): void {
  const lastActivityRef = useRef<number>(readLastActivityAt());
  const onIdleLogoutRef = useRef(onIdleLogout);
  onIdleLogoutRef.current = onIdleLogout;
  // Read at tick time so an idle timeout that lands mid-payment waits instead of dropping work.
  const isSafeToLogoutRef = useRef(isSafeToLogout);
  isSafeToLogoutRef.current = isSafeToLogout;

  useEffect(() => {
    if (!isActive || minutes <= 0) {
      return undefined;
    }

    const stamp = () => {
      const now = Date.now();
      lastActivityRef.current = now;
      writeLastActivityAt(now);
    };

    // The POS reloads itself (deferred refresh, maintenance, version bump); the persisted stamp
    // keeps those reloads from passing as activity on an abandoned device.
    lastActivityRef.current = readLastActivityAt();

    const events: Array<keyof WindowEventMap> = ['pointerdown', 'keydown', 'wheel', 'touchstart'];
    events.forEach((event) => window.addEventListener(event, stamp, { capture: true, passive: true }));

    const timeoutMs = minutes * 60_000;
    const intervalId = window.setInterval(() => {
      if (Date.now() - lastActivityRef.current < timeoutMs) {
        return;
      }
      if (!isSafeToLogoutRef.current) {
        return;
      }
      stamp();
      onIdleLogoutRef.current();
    }, TICK_MS);

    return () => {
      events.forEach((event) => window.removeEventListener(event, stamp, { capture: true }));
      window.clearInterval(intervalId);
    };
  }, [isActive, minutes]);
}
